import { useEffect, useMemo, useState } from 'react';
import {
	PaperAirplaneIcon,
	PlusIcon,
	XMarkIcon,
	LockClosedIcon,
	CheckCircleIcon,
	ClockIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Spinner } from '../ui';
import { invoiceApi } from '../../api';
import type { StaffBooking } from '../../types/staffBookings';

/**
 * Inline invoice panel under a staff booking card (mirrors htmls/Staff/22 oct/staff_permissions).
 *
 * The staff member reviews price, deposit and balance, may add discounts/surcharges, and sends.
 * The recipient is resolved by the backend from the customer on file: the masked phone/email
 * shown here are display-only, and which channels are used comes from the card's "Send to"
 * toggles. Sending needs INVOICE_SEND_OWN (or INVOICE_MANAGE_ALL); reviewing needs
 * INVOICE_CREATE_OWN — the backend enforces both and the assignment to the appointment.
 */
export interface StaffInvoicePanelProps {
	booking: StaffBooking;
	/** Channels chosen on the card. At least one is always on. */
	sendPhone: boolean;
	sendEmail: boolean;
	/** False for roles that may review but not send. */
	canSend: boolean;
	onSent: () => void;
}

type AdjustmentType = 'discount' | 'surcharge';

interface Adjustment {
	type: AdjustmentType;
	reason: string;
	amount: string;
}

const formatCurrency = (amount: number): string =>
	new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);

const formatHours = (minutes?: number | null): string => {
	if (!minutes) return '—';
	const hrs = minutes / 60;
	return `${Number.isInteger(hrs) ? hrs : hrs.toFixed(1)} hrs`;
};

const amountOf = (adj: Adjustment): number => {
	const n = parseFloat(adj.amount);
	return Number.isFinite(n) && n > 0 ? n : 0;
};

const StaffInvoicePanel = ({
	booking,
	sendPhone,
	sendEmail,
	canSend,
	onSent,
}: StaffInvoicePanelProps) => {
	const [adjustments, setAdjustments] = useState<Adjustment[]>([]);
	const [isLoadingExisting, setIsLoadingExisting] = useState(false);
	const [isSending, setIsSending] = useState(false);

	const servicePrice = booking.amount || 0;
	const deposit = booking.collectedDeposit || 0;
	const isPaid = booking.invoiceState === 'PAID';
	const isPending = booking.invoiceState === 'PENDING';

	// An existing invoice pre-fills the panel so a resend starts from what was sent.
	useEffect(() => {
		let cancelled = false;
		if (!booking.invoiceId) {
			setAdjustments([]);
			return;
		}
		setIsLoadingExisting(true);
		invoiceApi
			.getDetail(booking.invoiceId)
			.then(inv => {
				if (cancelled) return;
				const rows: Adjustment[] = [];
				if (inv.discount > 0) {
					rows.push({ type: 'discount', reason: '', amount: inv.discount.toFixed(2) });
				}
				if (inv.miscCharges > 0) {
					rows.push({
						type: 'surcharge',
						reason: inv.miscDescription || '',
						amount: inv.miscCharges.toFixed(2),
					});
				}
				setAdjustments(rows);
			})
			.catch(() => {
				// Review stays possible with a blank form; the resend still targets the invoice.
			})
			.finally(() => {
				if (!cancelled) setIsLoadingExisting(false);
			});
		return () => {
			cancelled = true;
		};
	}, [booking.invoiceId]);

	const discount = useMemo(
		() => adjustments.filter(a => a.type === 'discount').reduce((s, a) => s + amountOf(a), 0),
		[adjustments]
	);
	const surcharge = useMemo(
		() => adjustments.filter(a => a.type === 'surcharge').reduce((s, a) => s + amountOf(a), 0),
		[adjustments]
	);
	const balanceDue = servicePrice - deposit - discount + surcharge;

	const addAdjustment = () =>
		setAdjustments(prev => [...prev, { type: 'discount', reason: '', amount: '' }]);
	const removeAdjustment = (idx: number) =>
		setAdjustments(prev => prev.filter((_, i) => i !== idx));
	const updateAdjustment = (idx: number, patch: Partial<Adjustment>) =>
		setAdjustments(prev => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));

	const handleSend = async () => {
		if (!canSend || isPaid) return;
		if (!sendPhone && !sendEmail) {
			toast.error('Choose at least one way to send the invoice.');
			return;
		}
		if (discount > servicePrice - deposit) {
			toast.error(
				`Discount cannot exceed the amount still owed (${formatCurrency(Math.max(0, servicePrice - deposit))}).`
			);
			return;
		}
		setIsSending(true);
		try {
			const channels: string[] = [];
			if (sendEmail) channels.push('email');
			if (sendPhone) channels.push('sms');
			const miscDescription = adjustments
				.filter(a => a.type === 'surcharge' && a.reason.trim())
				.map(a => a.reason.trim())
				.join('; ');
			await invoiceApi.send({
				...(booking.invoiceId
					? { invoiceId: booking.invoiceId }
					: { appointmentId: booking.id }),
				discount,
				miscCharges: surcharge,
				miscDescription,
				sendVia: channels.join(','),
			});
			toast.success('Invoice sent successfully.');
			onSent();
		} catch {
			toast.error('Failed to send invoice. Please try again.');
		} finally {
			setIsSending(false);
		}
	};

	const channelNote = (() => {
		const phone = booking.customerPhone;
		const email = booking.customerEmail;
		if (sendPhone && sendEmail) {
			return (
				<>
					Invoice will be sent to both the client&apos;s phone{' '}
					{phone && <em className="not-italic font-semibold">({phone})</em>} and email{' '}
					{email && <em className="not-italic font-semibold">({email})</em>}.
				</>
			);
		}
		if (sendPhone) {
			return (
				<>
					Invoice will be sent to the client&apos;s phone{' '}
					{phone && <em className="not-italic font-semibold">({phone})</em>}.
				</>
			);
		}
		return (
			<>
				Invoice will be sent to the client&apos;s email{' '}
				{email && <em className="not-italic font-semibold">({email})</em>}.
			</>
		);
	})();

	return (
		<div
			onClick={e => e.stopPropagation()}
			className="w-full mt-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 overflow-hidden"
		>
			{/* Head */}
			<div className="flex items-center gap-3 flex-wrap px-4 py-3 border-b border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/60">
				<span className="text-sm font-bold text-gray-900 dark:text-white">🧾 Invoice</span>
				<span className="text-xs text-gray-500 dark:text-gray-400">
					APT-{booking.id} &middot; {booking.customerName}
				</span>
				<span className="ml-auto">
					{isPaid ? (
						<span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
							<CheckCircleIcon className="w-3.5 h-3.5" /> Paid
						</span>
					) : isPending ? (
						<span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
							<ClockIcon className="w-3.5 h-3.5" /> Payment Pending
						</span>
					) : (
						<span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
							Not sent
						</span>
					)}
				</span>
			</div>

			{/* Body */}
			<div className="px-4 py-3">
				{isLoadingExisting ? (
					<div className="flex items-center justify-center py-6">
						<Spinner size="sm" className="text-[#667eea]" />
					</div>
				) : (
					<>
						<Row label="Service" value={booking.serviceName || '—'} />
						<Row label="Duration" value={formatHours(booking.durationMinutes)} />
						<Row label="Service price" value={formatCurrency(servicePrice)} />
						{deposit > 0 && (
							<Row
								label="Deposit paid"
								value={`–${formatCurrency(deposit)}`}
								valueClass="text-emerald-600 dark:text-emerald-400"
							/>
						)}

						{/* Discounts & Adjustments */}
						<div className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
							Discounts &amp; Adjustments
						</div>
						<div className="flex flex-col gap-2 mt-2">
							{adjustments.map((adj, idx) => (
								<div
									key={idx}
									className="flex items-center gap-2 flex-wrap sm:flex-nowrap"
								>
									<select
										value={adj.type}
										disabled={isPaid}
										onChange={e =>
											updateAdjustment(idx, {
												type: e.target.value as AdjustmentType,
											})
										}
										className="px-2 py-1.5 rounded-md text-xs border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea]"
									>
										<option value="discount">🏷 Discount</option>
										<option value="surcharge">➕ Surcharge</option>
									</select>
									<input
										type="text"
										value={adj.reason}
										disabled={isPaid}
										onChange={e =>
											updateAdjustment(idx, { reason: e.target.value })
										}
										placeholder="Reason (e.g. loyalty, add-on)"
										className="flex-1 min-w-[140px] px-2.5 py-1.5 rounded-md text-xs border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#667eea]"
									/>
									<div className="flex items-center gap-1">
										<span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
											$
										</span>
										<input
											type="number"
											min="0"
											step="0.01"
											value={adj.amount}
											disabled={isPaid}
											onChange={e =>
												updateAdjustment(idx, { amount: e.target.value })
											}
											placeholder="0.00"
											className="w-24 px-2.5 py-1.5 rounded-md text-xs text-right font-semibold border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea]"
										/>
									</div>
									{!isPaid && (
										<button
											type="button"
											onClick={() => removeAdjustment(idx)}
											aria-label="Remove"
											className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer"
										>
											<XMarkIcon className="w-4 h-4" />
										</button>
									)}
								</div>
							))}
						</div>
						{!isPaid && (
							<button
								type="button"
								onClick={addAdjustment}
								className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#667eea] hover:underline cursor-pointer"
							>
								<PlusIcon className="w-3.5 h-3.5" /> Add Discount or Adjustment
							</button>
						)}

						<div className="mt-3 pt-3 border-t-2 border-[#667eea]/60 flex justify-between items-center">
							<span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
								Balance due
							</span>
							<span
								className={`text-base font-bold ${balanceDue < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-900 dark:text-white'}`}
							>
								{balanceDue < 0 ? '–' : ''}
								{formatCurrency(Math.abs(balanceDue))}
							</span>
						</div>

						{/* Delivery note */}
						<div className="mt-3 flex items-start gap-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300">
							<span className="flex-shrink-0">
								{sendPhone && '📱'}
								{sendEmail && '✉️'}
							</span>
							<span>
								{channelNote}
								{booking.customerContactMasked && (
									<span className="block mt-1 text-gray-500 dark:text-gray-400">
										<LockClosedIcon className="inline w-3 h-3 mr-1 align-[-2px]" />
										Contact details are hidden for your role; BBP delivers to
										the contact on file.
									</span>
								)}
							</span>
						</div>

						{/* Send */}
						{isPaid ? (
							<div className="mt-3 text-center text-xs font-semibold text-emerald-600 dark:text-emerald-400">
								✓ This invoice has been paid.
							</div>
						) : (
							<>
								<button
									type="button"
									onClick={handleSend}
									disabled={!canSend || isSending}
									className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
									style={{
										background:
											'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
									}}
								>
									{isSending ? (
										<Spinner size="sm" />
									) : (
										<>
											<PaperAirplaneIcon className="w-4 h-4" />
											{isPending
												? 'Resend Invoice to Client'
												: 'Send Invoice to Client'}
										</>
									)}
								</button>
								{!canSend && (
									<p className="mt-2 text-center text-xs text-amber-600 dark:text-amber-400">
										Your role can review this invoice but not send it.
									</p>
								)}
							</>
						)}
					</>
				)}
			</div>
		</div>
	);
};

const Row = ({
	label,
	value,
	valueClass,
}: {
	label: string;
	value: string;
	valueClass?: string;
}) => (
	<div className="flex justify-between items-center py-1.5 text-sm">
		<span className="text-gray-500 dark:text-gray-400">{label}</span>
		<span className={`font-semibold ${valueClass || 'text-gray-900 dark:text-white'}`}>
			{value}
		</span>
	</div>
);

export default StaffInvoicePanel;
